import { Stack } from '@mui/material'
import { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterContext } from '../../../components/data-grid/context'
import { useFormFilter } from '../../../hooks/form-filter'
import { SelectInput, Option } from '../../../inputs/select-input'

const types = ['all', 'normal', 'giftCard', 'pos'] as const
const statuses = ['', 'active', 'inactive'] as const

export type ApiKeyFilter = {
  type?: (typeof types)[number]
  status?: (typeof statuses)[number]
}

export const ApiKeyFilter = () => {
  const { t } = useTranslation('api_keys')
  const { setFilter, removeFilter, subscribeOnResetFilter, registerFilter } =
    useContext<FilterContext<ApiKeyFilter>>(FilterContext)

  const { queryParams, formContext, resetForm } = useFormFilter<ApiKeyFilter>({
    formProps: {
      defaultValues: { status: '', type: 'normal' },
    },
    parseOptions: {
      types: {
        status: (value) =>
          Object.values(statuses).some((x) => x === value) ? value : '',
        type: (value) =>
          Object.values(types).some((x) => x === value) ? value : '',
      },
    },
  })

  const typeOptions = useMemo<Option<Required<ApiKeyFilter>['type']>[]>(
    () => [
      { value: 'all', name: t('api_keys:filter.all') },
      { value: 'normal', name: t('api_keys:filter.type_normal') },
      { value: 'giftCard', name: t('api_keys:filter.type_gift_card') },
      { value: 'pos', name: t('api_keys:filter.type_pos') },
    ],
    [t],
  )

  const statusOptions = useMemo<Option<Required<ApiKeyFilter>['status']>[]>(
    () => [
      { value: '', name: t('api_keys:filter.all') },
      { value: 'active', name: t('api_keys:filter.enabled') },
      { value: 'inactive', name: t('api_keys:filter.disabled') },
    ],
    [t],
  )

  const { type, status } = queryParams

  useEffect(() => {
    registerFilter({ key: 'type' })
    registerFilter({ key: 'status' })
  }, [registerFilter])

  useEffect(() => {
    if (!type || type === 'normal') {
      return setFilter('type', {
        label: `${t('api_keys:filter.type')}: ${t('api_keys:filter.type_normal')}`,
        value: 'normal',
        isDefault: true,
      })
    }
    setFilter('type', {
      label: `${t('api_keys:filter.type')}: ${typeOptions.find((o) => o.value === type)?.name}`,
      value: type,
    })
  }, [type, setFilter, typeOptions, t, removeFilter])

  useEffect(() => {
    if (status) {
      setFilter('status', {
        label: `${t('api_keys:filter.status')}: ${
          statusOptions.find((o) => o.value === status)?.name
        }`,
        value: status,
      })
    } else {
      removeFilter('status')
    }
  }, [status, setFilter, statusOptions, t, removeFilter])

  useEffect(() => {
    const unsubscribe = subscribeOnResetFilter((key) => {
      if (!key || key === 'status' || key === 'type') {
        resetForm()
      }
    })

    return () => unsubscribe()
  }, [resetForm, subscribeOnResetFilter])

  return (
    <Stack width="100%" gap={2}>
      <SelectInput
        name="type"
        control={formContext.control}
        options={typeOptions}
        label={t('api_keys:type')}
        slotProps={{ select: { displayEmpty: true } }}
      />

      <SelectInput
        name="status"
        control={formContext.control}
        options={statusOptions}
        label={t('api_keys:status')}
        slotProps={{ select: { displayEmpty: true } }}
      />
    </Stack>
  )
}
